/**
 * PoolProvisionDialog — mettre un véhicule à disposition d'une autre unité.
 * =============================================================================
 * Lot 3.3 « le pool comme objet » (audit CD31 Q1/Q5, scène « SUT DDE → unité
 * Services une semaine »). Ouvert depuis la carte véhicule du « Parc partagé »,
 * côté GESTIONNAIRE.
 *
 * ⚠️ CE QUE CET ÉCRAN NE FAIT PAS, ET C'EST VOULU
 *
 * 1. IL NE DÉPLACE PAS L'ATTACHE. `vehicles.working_site_id` reste l'unité
 *    d'origine ; la mise à disposition est une FENÊTRE datée par-dessus. Le
 *    bandeau le dit à l'utilisateur, parce que c'est exactement ce qu'il croira
 *    avoir fait s'il ne le lit pas.
 * 2. IL N'ÉVINCE PERSONNE. Quand le serveur refuse en `PROVISION_CONFLICT`, il
 *    rend la LISTE des réservations qui bloquent : l'écran les affiche et
 *    propose d'aller les réassigner ou les annuler (gestes existants, portés par
 *    le parent), jamais un bouton « forcer » (D6). Un bouton « forcer » ici
 *    serait la première marche vers un pool où la promesse faite à un agent ne
 *    vaut rien.
 * 3. IL N'ENVOIE PAS D'HEURE. `from`/`to` sont des dates CIVILES, telles que
 *    l'`<input type="date">` les rend (`AAAA-MM-JJ`) : jamais un
 *    `new Date().toISOString()`, qui décalerait d'un jour selon le fuseau
 *    (ADR-013 §3). Le serveur refuse tout ce qui porte une heure.
 *
 * Contrat : `poolProvisionAPI.create` (`api/pool.js`) → `POST /pool/provisions`
 * → `backend/modules/pool/provision_service.mjs`. Refus lisibles ici :
 * `PROVISION_CONFLICT` (409), `VALIDATION` (400), 404 hors périmètre — même
 * contrat d'erreur (`message`, `code`, `data`) que le reste du module.
 *
 * @param {Object} props
 * @param {Object} props.vehicle - { vehicle_id|id, license_plate, make, model, home_site_id|site_id }
 * @param {Array} [props.sites] - les unités de l'org : [{ id, site_name }]
 * @param {() => void} props.onClose
 * @param {(provision: object) => void} [props.onProvisioned]
 * @param {(reservationId: number|string) => void} [props.onReassignReservation] - geste existant
 * @param {(reservationId: number|string) => void} [props.onCancelReservation] - geste existant
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { X, Share2, AlertTriangle, Info } from 'lucide-react';
import { poolProvisionAPI } from '../../api/pool.js';
import { useFormat } from '../../i18n/format.js';

/** `<input type="date">` rend déjà `AAAA-MM-JJ` : la valeur du jour se construit pareil. */
function todayCivil() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Le lendemain d'une date civile, sans passer par un fuseau (UTC pur, aller-retour). */
function nextCivilDay(civil) {
  const t = Date.parse(`${civil}T00:00:00Z`);
  if (Number.isNaN(t)) return civil;
  return new Date(t + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

const vehicleLabel = (vehicle) =>
  [vehicle?.license_plate, [vehicle?.make, vehicle?.model].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ');

function PoolProvisionDialog({
  vehicle,
  sites = [],
  onClose,
  onProvisioned,
  onReassignReservation,
  onCancelReservation,
}) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const queryClient = useQueryClient();

  const vehicleId = vehicle?.vehicle_id ?? vehicle?.id ?? null;
  // L'attache : `home_site_id` quand il vient de la disponibilité (lot 3.3),
  // `site_id`/`working_site_id` sinon. C'est la seule unité qu'on retire de la liste.
  const homeSiteId = vehicle?.home_site_id ?? vehicle?.working_site_id ?? vehicle?.site_id ?? null;

  const [toSiteId, setToSiteId] = useState('');
  const [from, setFrom] = useState(todayCivil);
  const [to, setTo] = useState(() => nextCivilDay(todayCivil()));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  /** Les unités possibles : celles de l'org, SAUF l'attache — on prête ailleurs. */
  const candidateSites = useMemo(
    () => (sites || []).filter((s) => homeSiteId == null || String(s.id) !== String(homeSiteId)),
    [sites, homeSiteId]
  );

  const homeSiteName = useMemo(
    () => (sites || []).find((s) => String(s.id) === String(homeSiteId))?.site_name || null,
    [sites, homeSiteId]
  );

  // Le serveur reste le juge (`from < to`, fenêtre passée, conflits) : ce
  // contrôle-ci n'est là que pour ne pas envoyer une requête manifestement vide.
  const rangeIsValid = Boolean(from) && Boolean(to) && from < to;
  const canSubmit = Boolean(vehicleId) && Boolean(toSiteId) && rangeIsValid && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await poolProvisionAPI.create({
        vehicle_id: vehicleId,
        to_site_id: toSiteId,
        from,
        to,
        reason: reason.trim() || undefined,
      });
      const provision = res?.data?.provision || res?.provision || res?.data || null;
      // Les compteurs changent de colonne TOUT DE SUITE, pas au prochain tic de 60 s.
      queryClient.invalidateQueries({ queryKey: ['pool-availability'] });
      queryClient.invalidateQueries({ queryKey: ['pool-provisions'] });
      queryClient.invalidateQueries({ queryKey: ['pool-schedule'] });
      onProvisioned?.(provision);
      onClose();
    } catch (err) {
      // `core.js` garde le message du module (`err.message`), son `code` et son `data`.
      const message = err.message || t('pool.provision.errors.generic');
      const conflicts = err.data?.conflicts || err.conflicts || null;
      setSubmitError({ message, code: err.code || null, conflicts });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="max-h-[90vh] w-[520px] max-w-full overflow-y-auto rounded-xl bg-white shadow-2xl" role="dialog" aria-label={t('pool.provision.title')}>
        <div className="flex items-center justify-between rounded-t-xl bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
          <div className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-white" />
            <h2 className="text-lg font-semibold text-white">{t('pool.provision.title')}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" aria-label={t('common.actions.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <p className="text-sm font-medium text-gray-900">{vehicleLabel(vehicle)}</p>

          {/* L'attache ne bouge pas — le dire AVANT, pas après le refus. */}
          <p className="flex items-start gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {homeSiteName
                ? t('pool.provision.hints.attachmentStaysNamed', { site: homeSiteName })
                : t('pool.provision.hints.attachmentStays')}
            </span>
          </p>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">{t('pool.provision.fields.toSite')}</span>
            <select
              value={toSiteId}
              onChange={(e) => setToSiteId(e.target.value)}
              required
              aria-label={t('pool.provision.fields.toSite')}
              className={fieldClass}
            >
              <option value="">{t('pool.provision.fields.toSitePlaceholder')}</option>
              {candidateSites.map((s) => (
                <option key={s.id} value={s.id}>{s.site_name || `#${s.id}`}</option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">{t('pool.provision.fields.from')}</span>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  const next = e.target.value;
                  setFrom(next);
                  if (next && to && next >= to) setTo(nextCivilDay(next));
                }}
                required
                aria-label={t('pool.provision.fields.from')}
                className={fieldClass}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">{t('pool.provision.fields.to')}</span>
              <input
                type="date"
                value={to}
                min={from ? nextCivilDay(from) : undefined}
                onChange={(e) => setTo(e.target.value)}
                required
                aria-label={t('pool.provision.fields.to')}
                className={fieldClass}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500">{t('pool.provision.hints.window')}</p>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">{t('pool.provision.fields.reason')}</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('pool.provision.fields.reasonPlaceholder')}
              aria-label={t('pool.provision.fields.reason')}
              className={fieldClass}
            />
          </label>

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              <p className="flex items-start gap-1.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <span className="font-medium">{t('pool.provision.refused.title')}</span> {submitError.message}
                </span>
              </p>

              {/* Les conflits : on les NOMME et on renvoie vers les gestes existants.
                  Jamais un bouton « forcer » — l'origine réassigne ou annule d'abord. */}
              {Array.isArray(submitError.conflicts) && submitError.conflicts.length > 0 && (
                <>
                  <p className="mt-2 text-xs font-medium text-red-800">{t('pool.provision.refused.conflictsTitle')}</p>
                  <ul className="mt-1 space-y-1">
                    {submitError.conflicts.map((c) => (
                      <li key={c.reservation_id} className="flex flex-wrap items-center gap-2 text-xs text-red-800">
                        <span>
                          {t('pool.provision.refused.conflictLine', {
                            from: c.planned_start ? fmt.dateTime(c.planned_start) : '—',
                            to: c.planned_end ? fmt.time(c.planned_end) : '—',
                          })}
                        </span>
                        {onReassignReservation && (
                          <button
                            type="button"
                            onClick={() => onReassignReservation(c.reservation_id)}
                            className="rounded border border-red-300 bg-white px-2 py-0.5 font-medium text-red-700 hover:bg-red-100"
                          >
                            {t('pool.provision.refused.reassign')}
                          </button>
                        )}
                        {onCancelReservation && (
                          <button
                            type="button"
                            onClick={() => onCancelReservation(c.reservation_id)}
                            className="rounded border border-red-300 bg-white px-2 py-0.5 font-medium text-red-700 hover:bg-red-100"
                          >
                            {t('pool.provision.refused.cancel')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {submitting ? t('pool.provision.actions.submitting') : t('pool.provision.actions.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PoolProvisionDialog;
